import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { MessageSquare, RefreshCcw } from 'lucide-react';
import type {
  Home,
  HomeAssignedChecklistItem,
  HomeCardRef,
  HomeHighlight,
  HomeUpNextItem,
  HomeWorkspaceRef,
} from '@molt/shared';
import {
  Button,
  CardSurface,
  EmptyState,
  Notice,
  Skeleton,
  TextField,
  profileName,
} from '@/components';
import { formatCardDate, type CardDateTranslate } from '@/lib/card-dates';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { cn } from '@/lib/cn';

export interface HomePageProps {
  readonly className?: string | undefined;
  readonly home: Home | undefined;
  readonly loading?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly workspaceFilterId?: string | null | undefined;
  readonly highlightWorkspaceOptions?: readonly HomeWorkspaceRef[] | undefined;
  readonly replyingHighlightId?: string | null | undefined;
  readonly checkingItemId?: string | null | undefined;
  readonly mutationError?: string | null | undefined;
  readonly onRetry: () => void;
  readonly onWorkspaceFilterChange: (workspaceId: string | null) => void;
  readonly onReplyToHighlight: (
    highlightId: string,
    cardId: string,
    boardId: string,
    body: string,
  ) => void;
  readonly onCheckAssignedItem: (itemId: string, cardId: string, boardId: string) => void;
}

export function HomePage({
  className,
  home,
  loading = false,
  error = null,
  workspaceFilterId = null,
  highlightWorkspaceOptions,
  replyingHighlightId = null,
  checkingItemId = null,
  mutationError = null,
  onRetry,
  onWorkspaceFilterChange,
  onReplyToHighlight,
  onCheckAssignedItem,
}: HomePageProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const workspaces = useMemo(
    () => highlightWorkspaceOptions ?? workspaceOptionsFromHome(home),
    [highlightWorkspaceOptions, home],
  );

  return (
    /*
     * ⚠ AN `<aside>` WITH AN h2, NOT A PAGE WITH AN h1. Home used to open the
     * route with its own h1 ABOVE the boards; it now sits beside them as a
     * companion column (`routes/index.tsx`), and `Boards` is the page's one h1
     * — the workspace groups below it must stay h2 (`FR-PERM-004.spec.ts`).
     */
    <aside
      aria-labelledby="home-title"
      className={cn('flex min-w-0 flex-col gap-4', className)}
    >
      <div className="flex flex-col gap-3">
        <div>
          <p className="text-xs font-medium text-ink-muted">{t('home.eyebrow')}</p>
          <h2 id="home-title" className="font-display text-xl font-semibold text-ink">
            {t('home.title')}
          </h2>
        </div>
        <label className="flex w-full flex-col gap-1 text-sm font-medium text-ink">
          {t('home.workspaceFilterLabel')}
          <select
            value={workspaceFilterId ?? ''}
            onChange={(event) =>
              onWorkspaceFilterChange(
                event.currentTarget.value === '' ? null : event.currentTarget.value,
              )
            }
            className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-ink shadow-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            <option value="">{t('home.allWorkspaces')}</option>
            {workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {displayText(workspace.name, t('home.untitledWorkspace'))}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error !== null ? (
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={3}
          title={t('home.loadFailedTitle')}
          body={error}
          action={
            <Button
              variant="primary"
              iconStart={<RefreshCcw aria-hidden="true" className="size-4" />}
              onClick={onRetry}
            >
              {t('home.retry')}
            </Button>
          }
        />
      ) : null}
      {mutationError !== null ? <Notice tone="bad" body={mutationError} /> : null}

      {loading ? (
        <div aria-busy="true" className="grid gap-4">
          <Skeleton shape="block" />
          <Skeleton shape="block" />
          <Skeleton shape="block" />
        </div>
      ) : home === undefined ? null : (
        <div className="flex flex-col gap-4">
          <HomeSection id="home-up-next" title={t('home.upNext.title')}>
            {home.upNext.length === 0 ? (
              <EmptyState
                tone="neutral"
                size="inline"
                headingLevel={4}
                title={t('home.upNext.emptyTitle')}
                body={t('home.upNext.emptyBody')}
              />
            ) : (
              <ol className="flex flex-col gap-2" aria-label={t('home.upNext.listLabel')}>
                {home.upNext.map((item) => (
                  <li key={upNextKey(item)}>
                    <CardLinkRow card={item.card} meta={upNextReason(t, item)} />
                  </li>
                ))}
              </ol>
            )}
          </HomeSection>

          <HomeSection id="home-highlights" title={t('home.highlights.title')}>
            {home.highlights.length === 0 ? (
              <EmptyState
                tone="neutral"
                size="inline"
                headingLevel={4}
                title={t('home.highlights.emptyTitle')}
                body={t('home.highlights.emptyBody')}
              />
            ) : (
              <PreviewList label={t('home.highlights.title')} items={home.highlights}>
                {(highlight, hidden) => (
                  <HighlightRow
                    key={highlight.id}
                    hidden={hidden}
                    highlight={highlight}
                    busy={replyingHighlightId === highlight.id}
                    onReplyToHighlight={onReplyToHighlight}
                  />
                )}
              </PreviewList>
            )}
          </HomeSection>

          <HomeSection
            id="home-assigned-checklist-items"
            title={t('home.assigned.title')}
          >
            {home.assignedChecklistItems.length === 0 ? (
              <EmptyState
                tone="neutral"
                size="inline"
                headingLevel={4}
                title={t('home.assigned.emptyTitle')}
                body={t('home.assigned.emptyBody')}
              />
            ) : (
              <PreviewList
                label={t('home.assigned.title')}
                items={home.assignedChecklistItems}
              >
                {(item, hidden) => (
                  <AssignedChecklistRow
                    key={item.id}
                    hidden={hidden}
                    item={item}
                    busy={checkingItemId === item.id}
                    onCheckAssignedItem={onCheckAssignedItem}
                  />
                )}
              </PreviewList>
            )}
          </HomeSection>
        </div>
      )}
    </aside>
  );
}

/**
 * ⚠ `FE-80.1` — `id` IS A PROP, and it stopped being derived from `title`.
 *
 * This component used to slug the heading id out of the title
 * (``home-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}``). That was
 * sound while the title was an English literal and becomes a defect the moment
 * it is a served catalogue value: the id turns into a function of the locale,
 * a Spanish `Próximo` slugs to `home-pr-ximo`, and — the real hazard — two
 * sections whose translations differ only OUTSIDE `[a-z0-9]` collapse onto one
 * id, at which point one region's `aria-labelledby` points at another
 * region's heading. Nothing in this repository can see that: the tests locate
 * regions by accessible name, never by id.
 *
 * The three ids passed above are byte-identical to what the old expression
 * produced in English, so this is a defect the routing pass EXPOSED rather
 * than caused, fixed inside the one file that owns it.
 */
function HomeSection({
  id,
  title,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly children: ReactElement;
}): ReactElement {
  return (
    <CardSurface
      variant="sheet"
      role="region"
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-3 p-3"
    >
      <h3 id={id} className="text-sm font-semibold text-ink">
        {title}
      </h3>
      {children}
    </CardSurface>
  );
}

/**
 * How many rows a Home section shows before "Show N more". Up Next is not
 * wrapped: the server already caps it (`HOME_UP_NEXT_LIMIT_DEFAULT`), which is
 * the cap `FR-NOTIF-027` :378 asks for. Highlights and assigned items arrive
 * 25 to a page, and an uncapped column of 25 is what buried the boards.
 */
export const HOME_SECTION_PREVIEW = 3;

/**
 * A list that shows its first `HOME_SECTION_PREVIEW` rows and a disclosure for
 * the rest. Client-side only — the query is unchanged, so its key still
 * matches `invalidateQueries(queryKeys.meHome())` after a reply or a tick.
 *
 * ⚠ THE OVERFLOW ROWS ARE `hidden`, NEVER SLICED AWAY. A Highlight row holds
 * its reply draft in its own state, so unmounting it throws the draft away —
 * on "Show fewer", and also with no click at all, when a refetch puts a newer
 * Highlight on top and pushes a row with an open draft past the preview.
 * Hidden rows stay mounted, keep their state, and leave the accessibility tree.
 */
function PreviewList<T>({
  label,
  items,
  children,
}: {
  readonly label: string;
  readonly items: readonly T[];
  readonly children: (item: T, hidden: boolean) => ReactElement;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const overflow = items.length - HOME_SECTION_PREVIEW;
  return (
    <>
      <ul id={listId} className="flex flex-col gap-2" aria-label={label}>
        {items.map((item, index) =>
          children(item, !expanded && index >= HOME_SECTION_PREVIEW),
        )}
      </ul>
      {overflow > 0 ? (
        <Button
          ref={toggleRef}
          variant="quiet"
          size="sm"
          className="self-start"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => {
            setExpanded((value) => !value);
            /* A pointer click does not focus a button in Safari, so focus can
               still be inside a row this click is about to hide. */
            toggleRef.current?.focus();
          }}
        >
          {expanded ? t('home.showFewer') : t('home.showMore', { count: overflow })}
        </Button>
      ) : null}
    </>
  );
}

function CardLinkRow({
  card,
  meta,
  className,
}: {
  readonly card: HomeCardRef;
  readonly meta: string;
  readonly className?: string | undefined;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <a
      href={card.path}
      className={cn(
        'block rounded-card border border-border bg-surface px-3 py-2 text-ink shadow-card transition-[filter,background-color]',
        'hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus dark:hover:brightness-110',
        className,
      )}
    >
      <span className="block text-sm font-semibold">
        {displayText(card.title, t('home.untitledCard'))}
      </span>
      <span className="block text-xs text-ink-muted">
        {meta} · {displayText(card.board.title, t('home.untitledBoard'))} ·{' '}
        {displayText(card.list.title, t('home.untitledList'))}
      </span>
    </a>
  );
}

function HighlightRow({
  highlight,
  hidden,
  busy,
  onReplyToHighlight,
}: {
  readonly highlight: HomeHighlight;
  readonly hidden: boolean;
  readonly busy: boolean;
  readonly onReplyToHighlight: HomePageProps['onReplyToHighlight'];
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const [reply, setReply] = useState('');
  /*
   * ⚠ The reply form is COLLAPSED until asked for. `FR-NOTIF-028` :388 wants
   * "an inline reply control", not an open textarea on every row — and one
   * per row is what made the feed several screens tall. The draft lives in
   * this row's state, so hiding the form keeps what was typed; and the form
   * stays open after a send, so the member sees the field clear.
   */
  const [replyOpen, setReplyOpen] = useState(false);
  const formId = useId();
  const titleId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const actor = profileName(highlight.actor);

  useEffect(() => {
    if (replyOpen) fieldRef.current?.focus();
  }, [replyOpen]);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const body = reply.trim();
    if (body === '') return;
    onReplyToHighlight(highlight.id, highlight.card.id, highlight.card.board.id, body);
    setReply('');
  }

  return (
    <li
      hidden={hidden}
      className="rounded-card border border-border bg-surface p-3 shadow-card"
    >
      <div className="flex flex-col gap-1.5">
        <a
          id={titleId}
          href={highlight.card.path}
          className="rounded-card text-sm font-semibold text-ink underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          {displayText(highlight.card.title, t('home.untitledCard'))}
        </a>
        {/*
          ⚠ ONE FLAT SENTENCE, and the two `font-medium` spans are gone with
          it. `interfaceMessage` returns a string and the catalogue has no
          rich-text arm, so a byline built out of markup cannot be translated
          at all; routing only the connective `on` would freeze word order and
          no locale could put the board before the actor. The card-title link
          directly above is this row's anchor, so the byline and the date share
          one muted line rather than competing with it.
        */}
        <p className="text-xs text-ink-muted">
          <span>
            {t('home.highlights.byline', {
              actor,
              board: displayText(highlight.card.board.title, t('home.untitledBoard')),
            })}
          </span>
          <span aria-hidden="true"> · </span>
          <span>{formatCardDate(t, highlight.createdAt)}</span>
        </p>
        {/* The clamp is on the inner span: on the padded box itself, overflow
            clips at the PADDING edge and a sliver of the fourth line shows. */}
        <p className="rounded-card bg-surface-raised px-2.5 py-1.5 text-sm text-ink">
          <span className="line-clamp-3">
            {displayText(highlight.comment.preview, t('home.highlights.noPreview'))}
            {highlight.comment.truncated ? '...' : ''}
          </span>
        </p>
        {/*
          Every row's toggle reads the same, so it is described by its row's
          card title — the name the form itself carries once open. And
          `aria-controls` only while the form exists: an IDREF to nothing is
          an error, not a hint. Focus is put back on the toggle when the form
          closes, because focus may have been inside it.
        */}
        <Button
          ref={toggleRef}
          size="sm"
          variant="quiet"
          className="self-start"
          iconStart={<MessageSquare aria-hidden="true" className="size-4" />}
          aria-expanded={replyOpen}
          aria-describedby={titleId}
          {...(replyOpen ? { 'aria-controls': formId } : {})}
          onClick={() => {
            if (replyOpen) toggleRef.current?.focus();
            setReplyOpen((open) => !open);
          }}
        >
          {replyOpen ? t('home.highlights.replyHide') : t('home.highlights.replyOpen')}
        </Button>
        {replyOpen ? (
          <form
            id={formId}
            aria-label={t('home.highlights.replyFormLabel', {
              title: displayText(
                highlight.card.title,
                t('home.highlights.replyFormFallback'),
              ),
            })}
            onSubmit={submit}
            className="flex flex-col gap-2"
          >
            <TextField
              ref={fieldRef}
              label={t('home.highlights.replyFieldLabel')}
              value={reply}
              onChange={(event) => setReply(event.currentTarget.value)}
              multiline
            />
            <Button
              type="submit"
              size="sm"
              variant="primary"
              className="self-end"
              busy={busy}
              disabled={reply.trim() === ''}
              disabledReason={t('home.highlights.replyDisabled')}
              iconStart={<MessageSquare aria-hidden="true" className="size-4" />}
            >
              {t('home.highlights.replySubmit')}
            </Button>
          </form>
        ) : null}
      </div>
    </li>
  );
}

function AssignedChecklistRow({
  item,
  hidden,
  busy,
  onCheckAssignedItem,
}: {
  readonly item: HomeAssignedChecklistItem;
  readonly hidden: boolean;
  readonly busy: boolean;
  readonly onCheckAssignedItem: HomePageProps['onCheckAssignedItem'];
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const progress = t('home.assigned.progress', {
    count: item.checklistCheckedItemCount,
    total: item.checklistItemCount,
  });
  return (
    <li
      hidden={hidden}
      className="rounded-card border border-border bg-surface p-3 shadow-card"
    >
      <div className="grid gap-3 sm:grid-cols-[auto_1fr]">
        <input
          type="checkbox"
          aria-label={t('home.assigned.checkItem', {
            item: displayText(item.text, t('home.assigned.itemFallback')),
          })}
          checked={false}
          disabled={busy}
          onChange={() => onCheckAssignedItem(item.id, item.card.id, item.card.board.id)}
          className={cn(
            'mt-0.5 size-5 rounded-card border border-border-strong accent-ink',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-wait',
          )}
        />
        <div className="min-w-0">
          <a
            href={item.card.path}
            className="rounded-card text-sm font-semibold text-ink underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            {displayText(item.text, t('home.assigned.untitledItem'))}
          </a>
          <p className="text-xs text-ink-muted">
            {displayText(item.checklist.title, t('home.assigned.untitledChecklist'))} ·{' '}
            {progress}
            {item.dueAt === null
              ? ''
              : ` · ${t('home.dueDate', { date: formatCardDate(t, item.dueAt) })}`}
          </p>
          <p className="text-xs text-ink-muted">
            {displayText(item.card.title, t('home.untitledCard'))} ·{' '}
            {displayText(item.card.board.title, t('home.untitledBoard'))}
          </p>
        </div>
      </div>
    </li>
  );
}

function upNextKey(item: HomeUpNextItem): string {
  return `${item.reason}:${item.card.id}`;
}

function upNextReason(t: CardDateTranslate, item: HomeUpNextItem): string {
  switch (item.reason) {
    case 'overdue':
      return t('home.overdueDate', { date: formatCardDate(t, item.card.dueAt) });
    case 'due_soon':
      return t('home.dueDate', { date: formatCardDate(t, item.card.dueAt) });
    case 'mentioned':
      return t('home.upNext.mentioned');
    case 'added_to_card':
      return t('home.upNext.addedToCard');
  }
}

function workspaceOptionsFromHome(home: Home | undefined): readonly HomeWorkspaceRef[] {
  if (home === undefined) return [];
  const map = new Map<string, HomeWorkspaceRef>();
  for (const card of [
    ...home.upNext.map((item) => item.card),
    ...home.highlights.map((item) => item.card),
    ...home.assignedChecklistItems.map((item) => item.card),
  ]) {
    map.set(card.board.workspace.id, card.board.workspace);
  }
  return [...map.values()];
}

function displayText(value: string, fallback: string): string {
  const trimmed = value.trim();
  return trimmed === '' ? fallback : trimmed;
}
